import * as React from 'react';
import { DisplayDate, DisplaySchedule } from '@tock/time';
import { IconCalendar, IconMapMarker } from '@tock/icons';
import { IconCalendar as IconCalendarLegacy, IconPin } from 'ui/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Typography } from 'omakase';
import { consumer, core } from '@tock/coretypes';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardDetails: {
      alignSelf: 'bottom',
    },
    cardListItem: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(1),
      '&:last-child': {
        marginBottom: 0,
      },
    },
    cardListItemIcon: {
      // In the event that a cardListItem's text ends up flowing onto multiple lines, align
      // the icon to the start of the flex space rather than the default center alignment.
      // This comes up specifically with complicated date rendering
      alignSelf: 'start',
      marginRight: theme.spacing(1),
    },
  })
);

interface Props {
  priceCents: consumer.IOfferedPriceSummary | undefined;
  date?: string;
  location: string;
  schedule?: core.Schedule;
}

export const CardDetails: React.FunctionComponent<Props> = ({
  priceCents,
  date,
  location,
  schedule,
}) => {
  const classes = useStyles();

  const repeats = schedule?.repetition?.length ?? 0 > 0;
  const multipleDates = schedule?.date?.length ?? 0 > 1;

  return (
    <section className={classes.cardDetails}>
      {/* this is so screen readers will read this text */}
      <Typography
        mb={1}
        className={classes.cardListItem}
        component="section"
        variant="body02"
        data-testid="cardLocation"
      >
        <IconMigrationUtil
          legacyIcon={<IconPin fontSize="small" className={classes.cardListItemIcon} />}
          newIcon={<IconMapMarker fontSize="small" className={classes.cardListItemIcon} />}
        />
        {location}
      </Typography>
      {date && (
        <Typography
          mb={1}
          className={classes.cardListItem}
          component="section"
          variant="body02"
          data-testid="cardDates"
        >
          <IconMigrationUtil
            legacyIcon={
              <IconCalendarLegacy fontSize="small" className={classes.cardListItemIcon} />
            }
            newIcon={<IconCalendar fontSize="small" className={classes.cardListItemIcon} />}
          />
          {repeats && schedule ? (
            <DisplaySchedule
              schedule={schedule}
              displayDateOptions={{
                displayMonthAs: 'short',
              }}
              /*We would like to use filterDatesEarlierThan, but don't have right date to supply. Leave unsdefined for now*/
              filterDatesEarlierThan={undefined}
            />
          ) : multipleDates && schedule ? (
            <>
              <DisplaySchedule
                schedule={schedule}
                displayDateOptions={{
                  displayMonthAs: 'short',
                }}
              />
            </>
          ) : (
            <DisplayDate dateValue={date} displayMonthAs="short" />
          )}
        </Typography>
      )}
    </section>
  );
};
